import { IFaucetConfig } from '../../common/FaucetConfig';
import { FaucetConfigContext, FaucetPageContext } from '../FaucetPage';
import React, { useContext } from 'react';
import { SlotOutlet } from '../../sdk/SlotOutlet';
import { useNavigate, NavigateFunction } from "react-router";
import { toReadableAmount } from '../../utils/ConvertHelpers';
import { renderDate, renderTime, renderTimespan } from '../../utils/DateUtils';
import getCountryIcon from 'country-flag-icons/unicode'
import { Tooltip } from 'react-bootstrap';
import { IFaucetContext } from '../../common/FaucetContext';
import { IClientClaimStatus, IClientSessionStatus, IFaucetRpcEndpointStatus, IFaucetStatusGeneralStatus, IFaucetStatusOutflowStatus, IFaucetStatusRefillStatus } from '../../types/FaucetStatus';
import { getOperatorKey, setOperatorKey } from '../../common/OperatorKey';

import "./FaucetStatus.css";
import { FaucetOverlay } from '../shared/FaucetOverlay';

export interface IFaucetStatusPageProps {
  pageContext: IFaucetContext;
  faucetConfig: IFaucetConfig;
  navigateFn: NavigateFunction;
}

export interface IFaucetStatusPageState {
  refreshing: boolean;
  /** what is in the key field; sent and stored when the form is submitted */
  keyInput: string;
  /** the list on the page came unmasked */
  unmasked: boolean;
  keyError: string | null;
  status: IFaucetStatusGeneralStatus;
  refillStatus: IFaucetStatusRefillStatus;
  outflowStatus: IFaucetStatusOutflowStatus;
  rpcEndpoints: IFaucetRpcEndpointStatus[];
  activeSessions: IClientSessionStatus[];
  activeClaims: IClientClaimStatus[];
}

export class FaucetStatusPage extends React.PureComponent<IFaucetStatusPageProps, IFaucetStatusPageState> {

  constructor(props: IFaucetStatusPageProps, state: IFaucetStatusPageState) {
    super(props);

    this.state = {
      refreshing: false,
      keyInput: getOperatorKey() || "",
      unmasked: false,
      keyError: null,
      status: null,
      refillStatus: null,
      outflowStatus: null,
      rpcEndpoints: [],
      activeSessions: [],
      activeClaims: [],
		};
  }

  public componentDidMount() {
    this.updateHeader();
    if(!this.state.refreshing) {
      this.refreshFaucetStatus();
    }
  }

  public componentDidUpdate(prevProps: IFaucetStatusPageProps, prevState: IFaucetStatusPageState) {
    if(prevState.unmasked !== this.state.unmasked || prevState.status !== this.state.status)
      this.updateHeader();
  }

  public componentWillUnmount() {
    this.props.pageContext.setPageHeader(null);
  }

  /** the card's header while this page is on */
  private updateHeader() {
    this.props.pageContext.setPageHeader({
      title: "Faucet status",
      meta: this.props.faucetConfig.faucetTitle + " \u00b7 " + (this.state.unmasked ? "real session ids and ips, a session id opens its details" : "session ids and ips are hashed"),
      pill: this.state.unmasked ? { text: "Unmasked", kind: "live" } : { text: "Masked", kind: "" },
    });
  }

  /** the key field was submitted: keep what is in it for the tab and reload with it */
  private onKeySubmit(evt: React.FormEvent) {
    evt.preventDefault();
    setOperatorKey(this.state.keyInput.trim() || null);
    this.refreshFaucetStatus();
  }

  private refreshFaucetStatus() {
    let key = getOperatorKey();
    this.setState({
      refreshing: true,
      keyError: null,
    });
    this.props.pageContext.faucetApi.getFaucetStatus(key || undefined).catch((err) => {
      // a refused key answers with a plain 403, which is no json: forget the key and show the
      // public list instead of a page that never loads
      if(!key)
        throw err;
      setOperatorKey(null);
      this.setState({ keyInput: "", keyError: "The faucet did not accept that key." });
      key = null;
      return this.props.pageContext.faucetApi.getFaucetStatus();
    }).then((faucetStatus) => {
      let activeClaims = (faucetStatus.claims || []).sort((a, b) => a.time - b.time);
      let activeClaimIds = {};
      activeClaims.forEach((claim) => {
        activeClaimIds[claim.session] = true;
      });

      let now = this.props.pageContext.faucetApi.getFaucetTime().getSyncedTime();
      let activeSessions = (faucetStatus.sessions || []).filter((session) => {
        if(session.start > now - 3600)
          return true;
        if(session.status === "failed")
          return false;
        if(session.status === "finished" && !activeClaimIds[session.id])
          return false;
        return true;
      }).sort((a, b) => a.start - b.start);

      this.setState({
        refreshing: false,
        unmasked: !!key,
        status: faucetStatus.status,
        refillStatus: faucetStatus.refill,
        outflowStatus: faucetStatus.outflowRestriction,
        rpcEndpoints: faucetStatus.rpcEndpoints || [],
        activeSessions: activeSessions,
        activeClaims: activeClaims,
      });
    }).catch((err) => {
      this.setState({
        refreshing: false,
      });
    });
  }

	public render(): React.ReactElement<IFaucetStatusPageProps> {
    return (
      <div className='container grid faucet-status'>
        <div className='status-head'>
          <form className='status-key' onSubmit={(evt) => this.onKeySubmit(evt)}>
            <label htmlFor="status-operator-key">Operator key</label>
            <input
              id="status-operator-key"
              type="password"
              className="form-control form-control-sm"
              autoComplete="off"
              value={this.state.keyInput}
              placeholder="none"
              onChange={(evt) => this.setState({ keyInput: evt.target.value })}
            />
            <button type="submit" className="btn btn-primary btn-sm status-refresh" disabled={this.state.refreshing}>Refresh</button>
          </form>
        </div>
        {this.state.keyError ?
          <div className='alert alert-warning status-key-error' role="alert">{this.state.keyError}</div>
        : null}
        <div className='row'>
        <div className='col-12 card status-panel'>
            <div className="card-body">
              <h5 className="card-title">Overview</h5>
              {this.renderFaucetStatus()}
            </div>
          </div>
          {this.state.rpcEndpoints.length > 0 ?
          <div className='col-12 card status-panel'>
            <div className="card-body">
              <h5 className="card-title">RPC Endpoints</h5>
              {this.renderRpcEndpoints()}
            </div>
          </div>
          : null}
          <div className='col-12 card status-panel'>
            <div className="card-body">
              <h5 className="card-title">Active mining sessions</h5>
              {this.renderActiveSessions()}
            </div>
          </div>
          <div className='col-12 card status-panel'>
            <div className="card-body">
              <h5 className="card-title">Reward claim transactions</h5>
              {this.renderActiveClaims()}
            </div>
          </div>
          <SlotOutlet slot="status.section" faucetConfig={this.props.faucetConfig} navigate={(path) => this.props.navigateFn(path)} />
        </div>
      </div>
    );
	}

  private renderFaucetStatus(): React.ReactElement {
    if(!this.state.status)
      return <span />;

    let sessionStatus = {
      mining: 0,
      hashrate: 0,
    };
    this.state.activeSessions.forEach((session) => {
      if(session.status === "running") {
        sessionStatus.mining++;
        if(session.hashrate > 0 && session.connected)
          sessionStatus.hashrate += session.hashrate;
      }
    });
    
    return (
      <div className="container status-general">
        <div className="row">
          <div className="col-xl-3 col-lg-4 col-6">
            <div className="status-block">
              <div className="status-prop">
                <span className="status-title">Total Sessions:</span>
                <span className="status-value">{this.state.activeSessions.length}</span>
              </div>
              <div className="status-prop">
                <span className="status-title">Mining Sessions:</span>
                <span className="status-value">{sessionStatus.mining}</span>
              </div>
              <div className="status-prop">
                <span className="status-title">Total Hashrate:</span>
                <span className="status-value">{Math.round(sessionStatus.hashrate * 100) / 100} H/s</span>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-lg-4 col-6">
            <div className="status-block">
              <div className="status-prop">
                <span className="status-title">Faucet Wallet Balance:</span>
                <span className="status-value">{toReadableAmount(this.state.status.walletBalance, this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}</span>
              </div>
              <div className="status-prop">
                <span className="status-title">Unclaimed Balance:</span>
                <span className="status-value">{toReadableAmount(this.state.status.unclaimedBalance, this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}</span>
              </div>
              <div className="status-prop">
                <span className="status-title">TX-Queue Balance:</span>
                <span className="status-value">{toReadableAmount(this.state.status.queuedBalance, this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}</span>
              </div>
              <div className="status-prop">
                <span className="status-title">Reward Restriction:</span>
                <span className="status-value">{Math.round(this.state.status.balanceRestriction * 1000) / 1000} %</span>
              </div>
            </div>
          </div>
          {this.state.outflowStatus ?
          <div className="col-xl-3 col-lg-4 col-6">
            <div className="status-block">
              <div className="status-prop">
                <span className="status-title">Outflow Limit:</span>
                <span className="status-value">{toReadableAmount(this.state.outflowStatus.amount, this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)} / {renderTimespan(this.state.outflowStatus.duration, 2)}</span>
              </div>
              <div className="status-prop">
                <span className="status-title">Outflow Balance:</span>
                <span className="status-value">{this.state.outflowStatus.balance > 0 ? "+" : "-"} {toReadableAmount(Math.abs(this.state.outflowStatus.balance), this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}</span>
              </div>
              <div className="status-prop">
                <span className="status-title">Balance Limits:</span>
                <span className="status-value">{toReadableAmount(this.state.outflowStatus.lowerLimit, this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)} / {toReadableAmount(this.state.outflowStatus.upperLimit, this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}</span>
              </div>
              <div className="status-prop">
                <span className="status-title">Outflow Restriction:</span>
                <span className="status-value">{Math.round(this.state.outflowStatus.restriction * 1000) / 1000} %</span>
              </div>
            </div>
          </div>
          : null}
          {this.state.refillStatus ?
          <div className="col-xl-3 col-lg-4 col-6">
            <div className="status-block">
              <div className="status-prop">
                <span className="status-title">Refill Contract Balance:</span>
                <span className="status-value">{toReadableAmount(this.state.refillStatus.balance, this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}</span>
              </div>
              <div className="status-prop">
                <span className="status-title">Refill Trigger Balance:</span>
                <span className="status-value">{toReadableAmount(this.state.refillStatus.trigger, this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}</span>
              </div>
              <div className="status-prop">
                <span className="status-title">Refill Amount:</span>
                <span className="status-value">{toReadableAmount(this.state.refillStatus.amount, this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}</span>
              </div>
              <div className="status-prop">
                <span className="status-title">Refill Cooldown:</span>
                <span className="status-value">{renderTimespan(this.state.refillStatus.cooldown, 3)}</span>
              </div>
            </div>
          </div>
          : null}
        </div>
      </div>
    );
  }

  private renderRpcEndpoints(): React.ReactElement {
    return (
      <table className="table table-striped status-sessions">
        <thead>
          <tr>
            <th scope="col">RPC URL</th>
            <th scope="col">Priority</th>
            <th scope="col">Metered</th>
            <th scope="col">Status</th>
            <th scope="col">Head Block</th>
            <th scope="col">Last Check</th>
            <th scope="col">Requests</th>
          </tr>
        </thead>
        <tbody>
          {this.state.rpcEndpoints.map((ep, idx) => this.renderRpcEndpointRow(ep, idx))}
        </tbody>
      </table>
    );
  }

  private renderRpcEndpointRow(ep: IFaucetRpcEndpointStatus, idx: number): React.ReactElement {
    let statusBadges: React.ReactElement[] = [];
    if (ep.ready)
      statusBadges.push(<span key="ready" className="badge bg-success">Ready</span>);
    else if (ep.online && ep.blockLag)
      statusBadges.push(<span key="lag" className="badge bg-warning text-dark">Block lag</span>);
    else
      statusBadges.push(<span key="offline" className="badge bg-danger">Offline</span>);

    let lastCheck = ep.lastCheck ? renderDate(new Date(ep.lastCheck * 1000), true, true) : "-";
    let urlNode: React.ReactElement = <span>{ep.url}</span>;
    if (ep.lastError) {
      urlNode = (
        <FaucetOverlay
          placement="auto"
          delay={{ show: 250, hide: 400 }}
          overlay={(props) => (
            <Tooltip id={"rpc-err-" + idx} {...props}>
              <div className='ipaddr-info claim-error'>{ep.lastError}</div>
            </Tooltip>
          )}
        >
          <span>{ep.url}</span>
        </FaucetOverlay>
      );
    }

    return (
      <tr key={idx}>
        <th scope="row">{urlNode}</th>
        <td>{ep.priority}</td>
        <td>{ep.metered ? "yes" : "no"}</td>
        <td>{statusBadges}</td>
        <td>{ep.blockHeight || "-"}</td>
        <td>{lastCheck}</td>
        <td>{ep.requestCount}</td>
      </tr>
    );
  }

  private renderActiveSessions(): React.ReactElement {
    return (
      <table className="table table-striped status-sessions">
        <thead>
          <tr>
            <th scope="col">{this.state.unmasked ? "Session" : "Session Hash"}</th>
            <th scope="col">{this.state.unmasked ? "IP" : "IP Hash"}</th>
            <th scope="col">Target Address</th>
            <th scope="col">Start Time</th>
            <th scope="col">Timeout</th>
            <th scope="col">Balance</th>
            <th scope="col">Nonce</th>
            <th scope="col">CliVer</th>
            <th scope="col">Boost</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {this.state.activeSessions.length > 0 ?
            this.state.activeSessions.map((session) => this.renderActiveSessionRow(session)) :
            <tr key="none">
              <th scope="row" colSpan={9}>No active sessions</th>
            </tr>
          }
        </tbody>
      </table>
    );
  }

  private renderActiveSessionRow(session: IClientSessionStatus): React.ReactElement {
    let sessionStatus: React.ReactElement[] = [];
    switch(session.status) {
      case "running":
        sessionStatus.push(<span key="running" className="badge bg-primary">Running</span>);
        if(!session.connected)
          sessionStatus.push(<span key="idle" className="badge bg-secondary">Idle{session.idle ? " (" + renderTime(new Date(session.idle * 1000)) + ")" : ""}</span>);
        if(session.hashrate > 0)
          sessionStatus.push(<span key="mining" className="badge bg-success">Mining ({Math.round(session.hashrate * 100) / 100} H/s)</span>);
        break;
      case "claimable":
        sessionStatus.push(<span key="claimable" className="badge bg-warning text-dark">Claimable</span>);
        break;
      case "claiming":
        sessionStatus.push(<span key="claiming" className="badge bg-info text-dark">Claiming</span>);
        break;
      case "finished":
        sessionStatus.push(<span key="finished" className="badge bg-success">Finished</span>);
        break;
      case "failed":
        sessionStatus.push(<span key="failed" className="badge bg-danger">Failed</span>);
        break;
      default:
        sessionStatus.push(<span key="status" className="badge bg-light text-dark">{session.status}</span>);
    }
    if(session.restr && (session.restr.reward < 100 || session.restr.blocked || session.restr.messages.length > 0)) {
      let restrClass: string;
      if(session.restr.blocked) 
        restrClass = "bg-danger";
      else if (session.restr.reward < 100)
        restrClass = "bg-warning";
      else
        restrClass = "bg-info";

      sessionStatus.push(
        <FaucetOverlay
          placement="auto"
          delay={{ show: 250, hide: 400 }}
          overlay={(props) => this.renderRestrictionInfo(session, props)}
        >
          <span key="limit" className={["badge", restrClass].join(" ")}>{session.restr.reward} %</span>
        </FaucetOverlay>
      );
    }

    if(session.factors && session.factors.length > 0) {
      let totalFactor = 1;
      session.factors.forEach((f) => {
        totalFactor *= f.factor;
      });
      sessionStatus.push(
        <FaucetOverlay
          placement="auto"
          delay={{ show: 250, hide: 400 }}
          overlay={(props) => this.renderFactorInfo(session, props)}
        >
          <span key="factor" className={["badge", "bg-info"].join(" ")}>x{Math.round(totalFactor * 100) / 100}</span>
        </FaucetOverlay>
      );
    }

    return (
      <tr key={session.id}>
        <th scope="row">{this.state.unmasked ? <a href={"#/details/" + session.id}>{session.id}</a> : session.id}</th>
        <td>
          <FaucetOverlay
            placement="right"
            delay={{ show: 250, hide: 400 }}
            overlay={(props) => this.renderSessionIpInfo(session, props)}
          >
            <span className='ipaddr'>
              {session.ipInfo && session.ipInfo.countryCode ? <span className='ipaddr-icon'>{getCountryIcon(session.ipInfo.countryCode)}</span> : null}
              {session.ip}
            </span>
          </FaucetOverlay>
        </td>
        <td>{session.target}</td>
        <td>{renderDate(new Date(session.start * 1000), true)}</td>
        <td>{renderDate(new Date((session.start + this.props.faucetConfig.sessionTimeout) * 1000), true)}</td>
        <td>{toReadableAmount(BigInt(session.balance), this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}</td>
        <td>{session.nonce}</td>
        <td>{session.cliver}</td>
        <td>{session.boost ? session.boost.factor : 1} ({session.boost ? session.boost.score : 0})</td>
        <td>{sessionStatus}</td>
      </tr>
    );
  }

  private renderSessionIpInfo(session: IClientSessionStatus, props: any): React.ReactElement {
    if(!session.ipInfo)
      return <span />;
    
    return (
      <Tooltip id="ipinfo-tooltip" {...props}>
        <div className='ipaddr-info'>
          <table>
            <tbody>
              {session.ipInfo.status !== "success" ?
                <tr>
                  <td colSpan={2} className='ipinfo-value'>{session.ipInfo.status}</td>
                </tr>
              : null}
              <tr>
                <td className='ipinfo-title'>Country:</td>
                <td className='ipinfo-value'>{session.ipInfo.country} ({session.ipInfo.countryCode})</td>
              </tr>
              <tr>
                <td className='ipinfo-title'>Region:</td>
                <td className='ipinfo-value'>{session.ipInfo.region} ({session.ipInfo.regionCode})</td>
              </tr>
              <tr>
                <td className='ipinfo-title'>City:</td>
                <td className='ipinfo-value'>{session.ipInfo.city} ({session.ipInfo.cityCode})</td>
              </tr>
              <tr>
                <td className='ipinfo-title'>ISP:</td>
                <td className='ipinfo-value'>{session.ipInfo.isp}</td>
              </tr>
              <tr>
                <td className='ipinfo-title'>Org:</td>
                <td className='ipinfo-value'>{session.ipInfo.org}</td>
              </tr>
              <tr>
                <td className='ipinfo-title'>AS:</td>
                <td className='ipinfo-value'>{session.ipInfo.as}</td>
              </tr>
              <tr>
                <td className='ipinfo-title'>Proxy:</td>
                <td className='ipinfo-value'>{session.ipInfo.proxy ? "yes" : "no"}</td>
              </tr>
              <tr>
                <td className='ipinfo-title'>Hosting:</td>
                <td className='ipinfo-value'>{session.ipInfo.hosting ? "yes" : "no"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Tooltip>
    );
  }

  private renderRestrictionInfo(session: IClientSessionStatus, props: any): React.ReactElement {
    if(!session.restr)
      return <span />;
    
    return (
      <Tooltip id="ipinfo-tooltip" {...props}>
        <div className='ipaddr-info'>
          <table>
            <tbody>
              <tr>
                <td className='ipinfo-title'>Reward:</td>
                <td className='ipinfo-value'>{session.restr.reward} %</td>
              </tr>
              {session.restr.blocked ?
                <tr>
                  <td className='ipinfo-title'>Blocked:</td>
                  <td className='ipinfo-value'>{session.restr.blocked}</td>
                </tr>
              : null}
              {session.restr.messages.map((message, idx) => {
                return (
                  <tr>
                    <td key={idx} colSpan={2} className='ipinfo-value'>{message.text}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Tooltip>
    );
  }

  private renderFactorInfo(session: IClientSessionStatus, props: any): React.ReactElement {
    if(!session.factors)
      return <span />;
    
    return (
      <Tooltip id="ipinfo-tooltip" {...props}>
        <div className='ipaddr-info'>
          <table>
            <tbody>
              {session.factors.map((f) => {
                return (
                  <tr>
                    <td className='ipinfo-title'>x{Math.round(f.factor * 100) / 100}</td>
                    <td className='ipinfo-value'>{f.module} {f.name ? "/" + f.name : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Tooltip>
    );
  }

  private renderActiveClaims(): React.ReactElement {
    return (
      <table className="table table-striped status-sessions">
        <thead>
          <tr>
            <th scope="col">Time</th>
            <th scope="col">{this.state.unmasked ? "Session" : "Session Hash"}</th>
            <th scope="col">To Address</th>
            <th scope="col">Amount</th>
            <th scope="col">Nonce</th>
            <th scope="col">TX Hash</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {this.state.activeClaims.length > 0 ?
            this.state.activeClaims.map((claim) => this.renderActiveClaimRow(claim)) :
            <tr key="none">
              <th scope="row" colSpan={7}>No active claims</th>
            </tr>
          }
        </tbody>
      </table>
    );
  }

  private renderActiveClaimRow(claim: IClientClaimStatus): React.ReactElement {
    let claimStatus: React.ReactElement = null;
    switch(claim.status) {
      case "queue":
        claimStatus = <span className="badge bg-secondary">Queued</span>;
        break;
      case "pending":
        claimStatus = <span className="badge bg-primary">Pending</span>;
        break;
      case "confirmed":
        claimStatus = <span className="badge bg-success">Confirmed</span>;
        break;
      case "failed":
        claimStatus = <FaucetOverlay
          placement="left"
          delay={{ show: 250, hide: 400 }}
          overlay={(props) => this.renderClaimFailInfo(claim, props)}
        >
          <span className="badge bg-danger">Failed</span>
        </FaucetOverlay>;
        break;
      default:
        claimStatus = <span className="badge bg-light text-dark">{claim.status}</span>;
    }

    return (
      <tr key={(claim.time + "-" + claim.target)}>
        <th scope="row">{renderDate(new Date(claim.time * 1000), true, true)}</th>
        <td>{this.state.unmasked ? <a href={"#/details/" + claim.session}>{claim.session}</a> : claim.session}</td>
        <td>{claim.target}</td>
        <td>{toReadableAmount(BigInt(claim.amount), this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}</td>
        <td>{claim.nonce || ""}</td>
        <td>{claim.hash || ""}</td>
        <td>{claimStatus}</td>
      </tr>
    );
  }

  private renderClaimFailInfo(claim: IClientClaimStatus, props: any): React.ReactElement {
    if(!claim.error)
      return null;
    
    return (
      <Tooltip id="ipinfo-tooltip" {...props}>
        <div className='ipaddr-info claim-error'>
          {claim.error}
        </div>
      </Tooltip>
    );
  }

}

export default (props) => {
  return (
    <FaucetStatusPage 
      {...props}
      pageContext={useContext(FaucetPageContext)}
      faucetConfig={useContext(FaucetConfigContext)}
      navigateFn={useNavigate()}
    />
  );
};

